// Web Push subscription helpers.
// Used by NotificationToggle and the notifications page to:
//   1. Prompt the user for notification permission
//   2. Subscribe with PushManager using the backend's VAPID public key
//   3. POST the subscription to /api/push/subscribe
//   4. Clear the home-screen icon badge when the user reads notifications

import { apiClient } from '../App';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * True when the app is running as an installed PWA (added to home screen on
 * iOS / installed on Android / installed as desktop PWA). This is the only
 * context where home-screen badges are meaningful.
 */
export const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  // iOS uses navigator.standalone; everything else uses the standalone media query
  if (window.navigator && window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
};

export const getCurrentPermission = () => {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
};

export async function getActiveSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Notifications are not supported in this browser.');
  }

  const perm =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;

  if (perm !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();

  if (!sub) {
    const { data } = await apiClient.get('/push/vapid-public-key');
    const applicationServerKey = urlBase64ToUint8Array(data.publicKey);
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  const subJson = sub.toJSON();
  await apiClient.post('/push/subscribe', {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    user_agent: navigator.userAgent,
  });

  return sub;
}

const sameKey = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

// Silently keeps an already-allowed device's subscription fresh: rotates it if the
// server key changed, and re-registers it so the server always has the live endpoint.
export async function refreshPushSubscription() {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) return; // never re-enable a device the user turned off
    const { data } = await apiClient.get('/push/vapid-public-key');
    const serverKey = urlBase64ToUint8Array(data.publicKey);
    const subKey = sub.options?.applicationServerKey;
    if (subKey && !sameKey(new Uint8Array(subKey), serverKey)) {
      await sub.unsubscribe().catch(() => {});
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey });
    }
    const subJson = sub.toJSON();
    await apiClient.post('/push/subscribe', {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      user_agent: navigator.userAgent,
    });
  } catch {
    /* best-effort */
  }
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const sub = await getActiveSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* noop */
  }
  try {
    await apiClient.post('/push/unsubscribe', { endpoint });
  } catch {
    /* noop */
  }
}

export async function clearAppBadge() {
  try {
    if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
      await navigator.clearAppBadge();
    } else if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      await navigator.setAppBadge(0);
    }
  } catch {
    /* not supported on this platform — silent ignore */
  }
}

export async function setAppBadge(count) {
  try {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      await navigator.setAppBadge(count);
    }
  } catch {
    /* not supported on this platform — silent ignore */
  }
}
